/**
 * Hooks para gestionar la configuración de requisitos de clases por club
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { clubClassConfigService } from "@/lib/services/clubClassConfig";
import { useAuthStore } from "@/stores/authStore";
import type { ClubClassRequirement } from "@/types";

// ==================== QUERY KEYS ====================

export const clubClassConfigKeys = {
  all: ["clubClassConfigs"] as const,
  byClub: (clubId: string) =>
    [...clubClassConfigKeys.all, "club", clubId] as const,
  byClubAndClass: (clubId: string, classId: string) =>
    [...clubClassConfigKeys.all, "club", clubId, "class", classId] as const,
};

// ==================== QUERIES ====================

/**
 * Obtener todas las configuraciones de un club
 */
export function useClubClassConfigs(clubId: string) {
  return useQuery({
    queryKey: clubClassConfigKeys.byClub(clubId),
    queryFn: () => clubClassConfigService.getByClub(clubId),
    enabled: !!clubId,
  });
}

/**
 * Obtener la configuración de una clase específica para un club
 */
export function useClubClassConfig(clubId: string, classId: string) {
  return useQuery({
    queryKey: clubClassConfigKeys.byClubAndClass(clubId, classId),
    queryFn: () => clubClassConfigService.getByClubAndClass(clubId, classId),
    enabled: !!clubId && !!classId,
  });
}

// ==================== MUTATIONS ====================

/**
 * Crear o actualizar la configuración de requisitos de una clase
 */
export function useUpsertClubClassConfig() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({
      clubId,
      classId,
      className,
      requirements,
    }: {
      clubId: string;
      classId: string;
      className: string;
      requirements: Omit<ClubClassRequirement, "id">[];
    }) =>
      clubClassConfigService.upsert(
        clubId,
        classId,
        className,
        requirements,
        user?.uid || "",
      ),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: clubClassConfigKeys.byClub(clubId),
      });
      toast.success("Requisitos guardados correctamente");
    },
    onError: (error) => {
      console.error("Error guardando requisitos:", error);
      toast.error("Error al guardar los requisitos");
    },
  });
}

/**
 * Añadir un requisito a una clase
 */
export function useAddRequirement() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({
      configId,
      requirement,
    }: {
      configId: string;
      requirement: Omit<ClubClassRequirement, "id">;
    }) =>
      clubClassConfigService.addRequirement(
        configId,
        requirement,
        user?.uid || "",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clubClassConfigKeys.all });
      toast.success("Requisito añadido");
    },
    onError: (error) => {
      console.error("Error añadiendo requisito:", error);
      toast.error("Error al añadir el requisito");
    },
  });
}

/**
 * Actualizar un requisito
 */
export function useUpdateRequirementConfig() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({
      configId,
      requirementId,
      updates,
    }: {
      configId: string;
      requirementId: string;
      updates: Partial<Omit<ClubClassRequirement, "id">>;
    }) =>
      clubClassConfigService.updateRequirement(
        configId,
        requirementId,
        updates,
        user?.uid || "",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clubClassConfigKeys.all });
      toast.success("Requisito actualizado");
    },
    onError: (error) => {
      console.error("Error actualizando requisito:", error);
      toast.error("Error al actualizar el requisito");
    },
  });
}

/**
 * Eliminar un requisito
 */
export function useDeleteRequirementConfig() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: ({
      configId,
      requirementId,
    }: {
      configId: string;
      requirementId: string;
    }) =>
      clubClassConfigService.deleteRequirement(
        configId,
        requirementId,
        user?.uid || "",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clubClassConfigKeys.all });
      toast.success("Requisito eliminado");
    },
    onError: (error) => {
      console.error("Error eliminando requisito:", error);
      toast.error("Error al eliminar el requisito");
    },
  });
}
